import { createFileRoute } from '@tanstack/react-router'
import { findOperator, mobileNormalize } from '@/lib/utils'

import OperatorsIcon from '@/components/OperatorIcons'
import { batchInsert } from '@/queries/phonebook'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@/components/ui/card'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import { type ColumnDef } from '@tanstack/react-table'
import { CheckIcon, XIcon } from 'lucide-react'
import { type ChangeEvent, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import {read, utils} from 'xlsx'
import { LoadingSpinner } from '@/components/ui/loading'
import { DataTable } from '@/components/ui/data-table'
import type { OperatorsName } from '@/stores/operator-store'

var regex = new RegExp('^(\\+98|0)?9\\d{9}$');


interface PhoneBookResult {
    name: string,
   phone_number: string,
    status: boolean,
    operator: string,
  }

export const Route = createFileRoute('/phone-book/import/')({
  component: ImportFromExcel,
})


function ImportFromExcel() {
  const navigate = Route.useNavigate();
  const [inProgress, setInProgress] = useState(false);
  const [result, setResult] = useState<Array<PhoneBookResult>>([]);

  const phoneBookBatchMutation = useMutation({
    mutationFn: (phoneNumbers: Array<any>) => batchInsert(phoneNumbers),
    mutationKey: ['batch', 'insert', 'phoneBooks'],
    onSuccess() {
      toast.success('دفترجه تلفن با موفقیت بروزرسانی شد');
      navigate({to: '/phone-book'});
    },
    onError(e: any) {
      console.log(e.response);
      if (e?.response?.data?.message && e.response.data.message.includes('exists')) {
        toast.error('یک یا چند شماره وارد شده تکراری می باشد');
      } else {
        toast.error('خطایی در بروزرسانی رخ داده است');
      }
    }
  });

  const columns: ColumnDef<PhoneBookResult>[] = [
    {
      header: 'نام مخاطب',
      accessorKey: 'name',
      cell: ({ getValue, row }) => {
        const [editing, setEditing] = useState(false);
        const [value, setValue] = useState(getValue() as string);
        return <>
          {editing ?
            <>
              <Input className='w-32' defaultValue={getValue() as string} value={value} onChange={(e) => setValue(e.target.value)}
                onBlur={() => {
                  if (value) {
                    setResult(results => results.map((result, index) => {
                      if (row.index == index) {
                        result.name = value;
                      }
                      return result;
                    }));
                  }
                } }
                onKeyDown={(e) => {
                  if (value && (e.key === 'Enter' || e.key === 'NumpadEnter')) {
                    setResult(results => results.map((result, index) => {
                      if (row.index == index) {
                        result.name = value;
                      }
                      return result;
                    }));
                  }
                } } />
              {!value && <span className='text-xs text-red-500'>وارد کردن این فیلد الزامی است</span>}
            </>
            :

            <button onDoubleClick={() => setEditing(true)}>
              {getValue() as string}
            </button>}

        </>;

      }
    },
    {
      header: 'شماره مخاطب',
      accessorKey: 'phone_number',
      cell: ({ getValue, row }) => {
        const [editing, setEditing] = useState(false);
        const [value, setValue] = useState(getValue() as string);
        return <>
          {editing ?
            <>
              <Input dir="ltr" phone autoFocus className='w-32' defaultValue={getValue() as string} value={value} onChange={(e) => setValue(e.target.value)}
                onBlur={() => {
                  if (value) {
                    setResult(results => results.map((result, index) => {
                      if (row.index == index) {
                        result.phone_number = value;
                        result.status = regex.test(value);
                        result.operator = findOperator(value) ?? '';
                      }
                      return result;
                    }));
                  }
                } }
                onKeyDown={(e) => {
                  if (value && (e.key === 'Enter' || e.key === 'NumpadEnter')) {
                    setResult(results => results.map((result, index) => {
                      if (row.index == index) {
                        result.phone_number = value;
                        result.status = regex.test(value);
                        result.operator = findOperator(value) ?? '';
                      }
                      return result;
                    }));
                  }
                } } />
              {!value && <span className='text-xs text-red-500'>وارد کردن این فیلد الزامی است</span>}
            </>
            :

            <button onDoubleClick={() => setEditing(true)}>
              {getValue() as string}
            </button>}

        </>;

      }
    },
    {
      header: 'اپراتور',
      accessorKey: 'operator',
      cell(props) {
        return <OperatorsIcon operator={props.getValue() as OperatorsName} operatorColors />;
      },
    },
    {
      header: 'وضعیت',
      accessorKey: 'status',
      cell: ({ getValue }) => {
        return getValue() ? <CheckIcon className='text-green-600' /> : <XIcon className='text-red-600' />;
      }
    }
  ];
  const onChangeFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      toast.error('فایلی انتخاب نشده است');
      return;
    }
    const files = Array.from(e.target.files);
    const reader = new FileReader();
    setInProgress(true);
    let workbook;
    reader.addEventListener('loadend', (e) => {
      setInProgress(false);
      workbook = read(e.target?.result, { type: 'binary' });

      // console.log(workbook.Sheets)
      const csv = utils.sheet_to_csv(workbook.Sheets[workbook.SheetNames[0]]);
      let columns = csv.split('\n');
      columns = columns.filter((_, i) => i !== 0);

      const data: Array<PhoneBookResult> = columns.map(column => {
        const splited = column.split(',');
        return {
          name: splited[0],
          phone_number: splited[1],
          status: regex.test(splited[1]),
          operator: findOperator(splited[1]) ?? ''
        };
      });

      setResult(data);
    });

    reader.addEventListener('error', (e) => {
      console.log(e)
      setInProgress(false);
      toast.error('خطایی در خواندن فایل مورد نظر رخ داده است');
    });
    reader.readAsBinaryString(files[0]);
  };

  const handleRestForm = () => {
    setResult([]);
  };

  const handleInsert = () => {
    phoneBookBatchMutation.mutate(result.filter(d => d.status == true).map(r => ({ phone_number: mobileNormalize(r.phone_number), name: r.name })));
  };

  return (
    <>
      <Card>
        <div className='flex flex-row items-center justify-between pl-4'>

          <CardHeader>
            <CardTitle>وارد کردن شماره از اکسل</CardTitle>
            <CardDescription>
              فایل اکسل خود را طبق نمونه پر کنید و در این جا وارد کنید
            </CardDescription>
          </CardHeader>
          <Button onClick={() => navigate({to: '/phone-book'})}>بازگشت</Button>
        </div>
        {inProgress ? <CardContent className='flex flex-col justify-center items-center space-x-7'>
          <LoadingSpinner size={20} />
          درحال بررسی فایل...
        </CardContent> : result.length > 0 ? <CardContent>
          <div className='flex justify-between items-center'>
            <div className="flex flex-col">

              <CardTitle>
                مخاطبین شناسایی شده از اکسل بارگذاری شده
              </CardTitle>
              <CardDescription>
                برای ویرایش شماره یا نام شناسایی شده روی آن دوبار کلیک کنید
              </CardDescription>
            </div>

            <div className='space-x-reverse space-x-4'>
              <Button variant={'outline'} onClick={handleRestForm}>بارگذاری مجدد</Button>
              <Button
                isLoading={phoneBookBatchMutation.isPending}
                onClick={handleInsert}
                disabled={phoneBookBatchMutation.isPending}>ارسال</Button>
            </div>
          </div>
          <div className='mt-4'>
          <DataTable columns={columns} data={result} pageCount={Math.ceil(result.length / 10)} />
          </div>

        </CardContent> : <CardContent className='space-y-4'>
          <a href={'../simplePhoneBook.xlsx'} download={'نمونه-وارد-کردن-اطلاعات'}><Button>دانلود فایل نمونه</Button></a>
          <div className='flex flex-col space-y-4 mt-4'>
            <Label>آپلود فایل اکسل</Label>
            <Input type="file"
              onChange={onChangeFile}
              accept='application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel' />
          </div>
        </CardContent>}
      </Card>
    </>
  );
}
